import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { ParamMap } from '@angular/router';
import { BlockActionType } from '@app/core/enums';
import { FilterExpandState, FilterType, ViewPart } from '@app/core/models';
import { ApiFilterService, FilterData } from '@app/core/services/api/api-filter.service';
import { ButtonHandlerService } from '@app/core/services/button-handler.service';
import { FiltersService } from '@app/shared/services/filters/filters.service';
import { LocalStorageService } from '@app/shared/services/storage';
import { Block } from '@core/models/block/block.model';
import { GlobalService } from '@core/services/common/global.service';
import { mergeAllStyles } from '@shared/helpers/block-helpers';
import { camelCase } from 'lodash';
import { BehaviorSubject, pipe, Subscription } from 'rxjs';
import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  FilterGroupSelectorOverlayComponent,
} from './filter-group-selector-overlay/filter-group-selector-overlay.component';

@Component({
  selector: 'app-filter-group-selector',
  styleUrls: ['./filter-group-selector.component.scss'],
  template: `
    <div #filterGroupSelector class="filter-group-selector" [formGroup]="form">
      <div *ngIf="expanderBlock"
           app-block
           [item]="expanderBlock"
           [appClickableBlock]="expanderBlock"
           [class]="expanderBlock.uId"
           [attr.tabindex]="block .tabindex"
           (click)="onClickLabel($event)"
           [ngStyle]="{cursor: itemsLoaded ? 'pointer' : null}">
      </div>
      <div #overlayAnchor class="overlay-anchor"></div>
    </div>

  `,
  providers: [ButtonHandlerService],
  standalone: false,
})
export class FilterGroupSelectorComponent implements OnChanges, OnInit, OnDestroy, DoCheck, AfterViewInit {
  form: FormGroup;
  @ViewChild('filterGroupSelector') filterGroupSelector: ElementRef<HTMLInputElement>;
  @ViewChild('overlayAnchor') overlayAnchor: ElementRef<HTMLInputElement>;
  @Input() block: Block;
  expanderBlock: Block;
  itemTemplate: Block;
  itemsSubject = new BehaviorSubject<FilterData[]>(null);
  items: FilterData[] = [];
  itemsLoaded = false;
  arrayFromBehaviorSubject: any;
  getListAPI = true;
  initialExpandStep1 = false;
  initialExpandStep2 = false;
  initialExpandPerformed = false;
  @Input() extraData: any = {};
  selectorOverlay: OverlayRef | null = null;
  selectorComponent: FilterGroupSelectorOverlayComponent;
  protected scrollEnabled = false;
  protected scrollEnabledTimeout = null;
  private _filterSubscription = Subscription.EMPTY;

  constructor(
    private _fb: UntypedFormBuilder,
    private fs: FiltersService,
    private afs: ApiFilterService,
    public localStorageService: LocalStorageService,
    private buttonHandlerService: ButtonHandlerService,
    private overlay: Overlay,
    private globalService: GlobalService,
  ) {
  }

  get controlType(): FilterType {
    return this.getControlType(this.block);
  }

  get controlTypeArray() {
    return this.form.get(this.controlType) as FormArray;
  }

  private _expanded = false;

  @Input() get expanded(): boolean {
    return this._expanded;
  }

  set expanded(expanded: boolean) {
    // Only emit events and update the internal value if the value changes.
    if (this._expanded !== expanded) {
      this._expanded = expanded;
      this.updateOverlayVisibility();
      this.updateScroll();
    }
  }

  ngDoCheck(): void {
    const arrayFromBehaviorSubject2 = this.fs.listCategory.getValue();
    if (this.arrayFromBehaviorSubject !== arrayFromBehaviorSubject2) {

      this.itemsLoaded = false;
      const groupType = this.getControlType(this.block);
      this.fs.listCategory.pipe(
        map((data: any) => {
          if (data) {
            return data[`${groupType}Name`];
          } else {
            return '';
          }
        }),
      ).subscribe((list: FilterData[]) => {
        if (this.parseSelectFilter(groupType)) {
          const items = list ? list : [];
          this.initializeFormArray(items);
          this.itemsSubject.next(items);
          this.items = items;
          const selectItem: any = items.find(e => e.id === this.fs.paramsSelectFiters[`${groupType}`]);
          if (selectItem) {
            selectItem['active'] = true;
            this.onItemChecked(selectItem);
          }
        }
        this.updateOverlayItems();
        this.initialExpandStep2 = true;
        this.initialExpand();
      });
      this.arrayFromBehaviorSubject = arrayFromBehaviorSubject2;
    }

    if (!arrayFromBehaviorSubject2 && this.getListAPI) {
      this.getListAPI = false;
      const groupType = this.getControlType(this.block);
      this.fs.listCategory.subscribe(data => {
        if (!data) {
          this.afs
            .getList(groupType)
            .pipe(
              take(1),
              tap((list) => this.initializeFormArray(list)),
              tap((list) => this.itemsSubject.next(list)),
            ).subscribe((list: FilterData[]) => {
            this.items = list ? list : [];
            this.updateOverlayVisibility();
            this.updateScroll();
          });
        }
      });
    }
    if (this.localStorageService.closeModalFilter) {
      this.expanded = false;
    }
  }

  ngOnInit(): void {
    this._filterSubscription = this.itemsSubject
      .pipe(
        withLatestFrom(this.fs.filtersFrom$),
        pipe(map(([items, filters]: any[]) => filters)),
        tap((p: ParamMap) => this.updated(p)),
      )
      .subscribe();
    this.buttonHandlerService.click$
      .pipe(
        filter(({actionType}) => actionType === BlockActionType.CLICK),
        tap(() => this.toggleExpand()),
      )
      .subscribe();

    this.fs.filtersFrom$.subscribe((value: ParamMap) => {
      const current = value.getAll(this.controlType);
      if (!current) {
        this.controlTypeArray.controls.forEach(item => {
          item.patchValue({active: false});
        });
      } else {
        this.controlTypeArray.controls.forEach(item => {
          const val = current.map(id => id.toString());
          const isSelected = val.includes(item.value.id.toString());
          item.patchValue({active: isSelected});
        });
      }
    });
  }

  parseSelectFilter(groupType: FilterType) {
    if (this.fs.selectLastFilter === groupType) {
      return !this.fs.paramsSelectFiters[`${groupType}`];
    } else {
      return true;
    }
  }

  ngAfterViewInit() {
    this.initialExpandStep1 = true;
    this.initialExpandStep2 = true;
    this.initialExpand();
  }

  initialExpand() {
    if (this.initialExpandPerformed !== true && this.initialExpandStep1 && this.initialExpandStep2) {
      this.initialExpandPerformed = true;
      this.expanded = this.expandedStateToBoolean(this.getExpandDefaultState(this.block));
    }
  }

  showOverlay() {
    if (this.selectorOverlay !== null) {
      this.selectorOverlay.detach();
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.overlayAnchor)
      .withPositions([{
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
      }]);

    const scrollStrategy = this.overlay.scrollStrategies.reposition();

    const overlayConfig = {
      hasBackdrop: false,
      positionStrategy,
      scrollStrategy,
    };

    this.selectorOverlay = this.overlay.create(overlayConfig);
    const component = new ComponentPortal(FilterGroupSelectorOverlayComponent);
    const componentRef = this.selectorOverlay.attach(component);
    this.selectorComponent = componentRef.instance;
    this.selectorComponent.outerStyle = this.getExpansionPanelOuterStyles();
    this.selectorComponent.innerStyle = this.getExpansionPanelInnerStyles();
    this.selectorComponent.filterGroupSelector = this.filterGroupSelector;
    this.selectorComponent.filterGroupSelectorStyles = this.extraData.filterGroupSelectorStyles;
    this.selectorComponent.controlType = this.controlType;
    this.selectorComponent.form = this.form;
    this.selectorComponent.items = this.items;
    this.selectorComponent.itemTemplate = this.itemTemplate;
    this.selectorComponent.itemChecked.subscribe((e) => {
      if (e['$event']?.id !== e['$item']?.id) {
        e['$event'].id = e['item']?.id;
      }
      this.onItemChecked(e['$event']);
    });
    this.selectorComponent.collapsed.subscribe(() => this.killOverlay());
  }

  updateOverlayItems() {
    if (this.selectorComponent) {
      this.selectorComponent.items = this.items;
    }
  }

  hideOverlay() {
    if (this.selectorComponent) {
      // start the overlay collapsing
      this.selectorComponent.collapse();
    }
  }

  killOverlay() {
    if (this.selectorOverlay === null) {
      return;
    }
    this.selectorOverlay.detach();
    this.selectorComponent = null;
    this.selectorOverlay = null;
  }

  updateScroll() {
    // use timeout to enable scrolling after animation is finished to avoid flickering scrollbar
    if (this.scrollEnabledTimeout !== null) {
      clearTimeout(this.scrollEnabledTimeout);
      this.scrollEnabledTimeout = null;
    }
    if (this.expanded) {
      this.scrollEnabledTimeout = setTimeout(() => {
        this.scrollEnabled = true;
        this.scrollEnabledTimeout = null;
      }, 600);
    } else {
      this.scrollEnabled = false;
    }
  }

  updateOverlayVisibility() {
    if (this.expanded) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }
  }

  getExpansionPanelOuterStyles() {
    const styles: any = {};
    if (this.filterGroupSelector?.nativeElement?.offsetWidth > 0) {
      styles.width = `${this.filterGroupSelector.nativeElement.offsetWidth}px`;
    }
    return styles;
  }

  getExpansionPanelInnerStyles() {
    const styles: any = {};
    if (typeof this.extraData.filterGroupSelectorStyles.all.basic.width === 'string') {
      const querySnakeCase = camelCase(this.globalService.mediaQuery.value.query);
      const mergedStyles = mergeAllStyles(this.extraData.filterGroupSelectorStyles, querySnakeCase);
      styles.width = mergedStyles.basic.width;
    }
    return styles;
  }

  onClickLabel(event: any) {
    if (!event.target.classList.contains('mat-icon')) {
      this.expanded = !this.expanded;
    }
  }

  ngOnChanges(): void {
    if (!this.block) {
      return;
    }

    if (!this.initializeHeaderViewPart(ViewPart.HEADER_TEMPLATE)) {
      throw new Error('header template missing');
    }

    if (!this.initializeItemViewPart(ViewPart.ITEM_TEMPLATE)) {
      throw new Error('item template missing');
    }

    const groupType = this.getControlType(this.block);
    this.form = this._fb.group({
      [groupType]: this._fb.array([]),
    });
  }

  ngOnDestroy(): void {
    this.hideOverlay();
    this._filterSubscription.unsubscribe();
  }

  onItemChecked(event: { id: number; name: string; active: boolean }) {
    const {controlType} = this.block.settings;
    this.fs.selectLastFilter = controlType;
    this.fs.updateParams(event.active, controlType, event.id);
  }

  private getControlType = (block: Block): FilterType => {
    const {
      settings: {controlType},
    } = block;
    return controlType;
  };

  private getExpandDefaultState = (block: Block): FilterExpandState => {
    const {
      settings: {filterExpandState},
    } = block;
    return filterExpandState;
  };

  private expandedStateToBoolean(state: FilterExpandState): boolean {
    return state === FilterExpandState.EXPAND;
  }

  private toggleExpand(): void {
    this.expanded = !this.expanded;
  }

  private updated(params: ParamMap): void {
    const {controlType} = this.block.settings;
    const values = params.getAll(controlType);

    for (const key of values) {
      const item = this.controlTypeArray.controls.find(
        ({value}) => value.id.toString() === key,
      );

      item?.patchValue({active: true});
    }
  }

  private initializeFormArray(list: FilterData[]) {
    this.controlTypeArray.clear();
    for (const item of list) {
      const group = this.typeForm();
      group.patchValue(item);
      this.controlTypeArray.push(group);
    }
  }

  private initializeHeaderViewPart(name: ViewPart) {
    this.expanderBlock = this.block.children.find(
      ({settings: {viewPart}}) => viewPart === name,
    );
    return this.expanderBlock;
  }

  private initializeItemViewPart(name: ViewPart) {
    this.itemTemplate = this.block.children.find(
      ({settings: {viewPart}}) => viewPart === name,
    );
    return this.itemTemplate;
  }

  private typeForm() {
    return this._fb.group(
      {
        id: [],
        name: [''],
        active: [false],
      },
      {updateOn: 'change'},
    );
  }
}
